import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Button, InputLabel, Menu, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import TitleIcon from "@mui/icons-material/Title";
import PunchClockIcon from "@mui/icons-material/PunchClock";
import ContainerItem from "./ContainerItem";
import {
  setBPLRTComponents,
  setContentLayers,
  setLayerArray,
  setLayerMedias,
  setTextLayers,
} from "../../../../store/actions/cmsAction";
import { TypographyNormal } from "../../../customComponent";
import { colorStyling } from "../../../../helpers/color";
import { logger } from "../../../../helpers/logger";
import { initiateAXIOS } from "../../../../config/axios";
import { assetCMSAPILimit } from "../../../../api";
import {
  bplrtFlag,
  stellarFlag,
} from "../../../../externalFeatures/componentsFlag";
import update from "immutability-helper";
import { v4 as uuidv4 } from "uuid";

const StyledBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  ":last-child": {
    marginBottom: 0,
  },
}));

const ContainersList = ({ resType }) => {
  const dispatch = useDispatch();
  const {
    contentLayers,
    textLayers,
    layerMedias,
    layerArray,
    contentRes,
    widthRatio,
    heightRatio,
  } = useSelector((state) => state.cms);

  const { t } = useTranslation("cms");

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // CURRENTLY NOT USED, KEPT FOR FUTURE USE
  // eslint-disable-next-line no-unused-vars
  const getLatestBPLRTComponents = async (appType) => {
    const access_token = sessionStorage.getItem("access_token");
    const getUrl = `${assetCMSAPILimit}&offset=0&sortBy=idDesc&type=${appType}`;
    try {
      const { data } = await initiateAXIOS.get(getUrl, {
        headers: { authorization: `Bearer ${access_token}` },
      });

      const latestItem = data.items?.[0];
      logger.log(`get latest id for "${appType}"`, latestItem);

      if (latestItem) {
        dispatch(
          setBPLRTComponents({
            add: { [appType]: latestItem },
          })
        );
      }
    } catch (error) {
      logger.error(`ERR - get latest id for "${appType}":`, error);
    }
  };

  useEffect(() => {
    if (bplrtFlag) {
      // CURRENTLY NOT USED, KEPT FOR FUTURE USE
      // sessionStorage.setItem("itxcast-id", "BP1"); // NOT WORKING
      // const allOptions = { ...bplrtOptions, BPLRT_TICKER: true };
      // Object.keys(allOptions).forEach((optionType) => {
      //   getLatestBPLRTComponents(optionType);
      // });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addContainer = useCallback(
    (type) => {
      const newLayer = {
        id: uuidv4(),
        name: "",
        type: type,
        active: false,
        dragFlag: false,
        transition: "NONE",
      };

      const layerCount = Object.keys(contentLayers).length;
      const initDim = {
        left: 0,
        top: 0,
        zIndex: layerCount + 1,
      };

      switch (type) {
        case "media":
          newLayer["bgColor"] = colorStyling.cms.black;
          initDim["width"] = Math.round(200 * widthRatio);
          initDim["height"] = Math.round(200 * heightRatio);
          if (initDim["width"] > contentRes.width) {
            initDim["width"] = contentRes.width;
          }
          if (initDim["height"] > contentRes.height) {
            initDim["height"] = contentRes.height;
          }
          if (stellarFlag) initDim["advertisements"] = [];
          initDim["lockRatio"] = false;
          break;

        case "text":
          newLayer["bgColor"] = colorStyling.cms.darkgray;
          initDim["width"] = contentRes.width;
          initDim["height"] = Math.round(50 * heightRatio);
          initDim["lockRatio"] = false;
          const initText = {
            text: t("designer.enterTextPlaceholder"),
            family: "ARIAL",
            size: "44",
            weight: "400",
            color: colorStyling.cms.black,
            hAlign: "LEFT",
            vAlign: "CENTER",
            scrolling: true,
            direction: "LEFT",
            speed: 1,
          };

          newLayer["text"] = initText;
          dispatch(setTextLayers({ add: { [newLayer.id]: { edit: false } } }));
          break;

        case "ticker":
          newLayer["name"] = "BPLRT Ticker";
          newLayer["type"] = "ticker";
          initDim["width"] = contentRes.width;
          initDim["height"] = Math.round(20 * heightRatio);
          initDim["lockRatio"] = false;
          break;

        default:
          break;
      }

      newLayer["dimensions"] = initDim;
      const updatedLayers = update(contentLayers, {
        $merge: { [newLayer.id]: newLayer },
      });

      const updatedLayerArray = [...layerArray];
      updatedLayerArray.push(newLayer.id);
      dispatch(setLayerArray(updatedLayerArray));
      dispatch(setContentLayers({ ...updatedLayers }));
      handleCloseMenu();
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentLayers, contentRes, widthRatio, heightRatio]
  );

  const removeContainer = useCallback(
    (id, type) => {
      const updatedLayerArray = layerArray.filter((layerID) => layerID !== id);
      dispatch(setLayerArray(updatedLayerArray));

      const removedLayerZ = contentLayers[id].dimensions.zIndex;
      const layerKeys = Object.keys(contentLayers);
      let updatedContentLayers = { ...contentLayers };
      layerKeys.forEach((layerID) => {
        const layer = contentLayers[layerID];
        let adjustedDim = { ...layer.dimensions };
        if (removedLayerZ < adjustedDim.zIndex) {
          adjustedDim.zIndex -= 1;
          updatedContentLayers = update(updatedContentLayers, {
            [layerID]: {
              dimensions: {
                $merge: adjustedDim,
              },
            },
          });
        }
      });

      updatedContentLayers = update(updatedContentLayers, { $unset: [id] });
      dispatch(setContentLayers({ ...updatedContentLayers }));

      const updatedMedias = update(layerMedias, { $unset: [id] });
      dispatch(setLayerMedias({ forceUpdate: { ...updatedMedias } }));
      if (type === "text") {
        const updatedTextLayers = update(textLayers, { $unset: [id] });
        dispatch(setTextLayers({ ...updatedTextLayers }));
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentLayers, layerMedias, textLayers]
  );

  return (
    <Box sx={{ mb: 8 }}>
      <StyledBox>
        <InputLabel>{t("designer.layerListLabel")}</InputLabel>

        <Box sx={{ mt: 2 }}>
          {Object.keys(contentLayers).map((key, index) => {
            const { id } = contentLayers[key];
            return (
              <ContainerItem
                key={id}
                index={index + 1}
                container={contentLayers[key]}
                removeContainer={removeContainer}
              />
            );
          })}
        </Box>

        <Box
          sx={{ width: "100%" }}
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          {resType === "" && (
            <p style={{ color: "red" }}>{t("designer.selectResHint")}</p>
          )}

          <Button
            variant="contained"
            endIcon={<KeyboardArrowDownIcon />}
            sx={{
              boxShadow: 3,
              marginTop: resType === "" ? 2 : undefined,
            }}
            disabled={resType === ""}
            onClick={handleOpenMenu}
          >
            {t("designer.layerAddBtn")}
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleCloseMenu}
            elevation={0}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            transformOrigin={{ horizontal: "left", vertical: "top" }}
            PaperProps={{
              sx: {
                boxShadow: 3,
              },
            }}
            sx={{ marginTop: 1 }}
          >
            <MenuItem
              onClick={() => addContainer("media")}
              sx={{ display: "flex" }}
            >
              <PermMediaIcon sx={{ mx: 1 }} />
              <TypographyNormal sx={{ ml: 1 }}>
                {t("designer.layerAddMediaApps")}
              </TypographyNormal>
            </MenuItem>

            <MenuItem
              onClick={() => addContainer("text")}
              sx={{
                display: "flex",
              }}
            >
              <TitleIcon sx={{ mx: 1 }} />
              <TypographyNormal sx={{ ml: 1 }}>
                {t("designer.layerAddText")}
              </TypographyNormal>
            </MenuItem>

            {bplrtFlag && (
              <MenuItem
                onClick={() => addContainer("ticker")}
                sx={{
                  display: "flex",
                }}
              >
                <PunchClockIcon sx={{ mx: 1 }} />
                <TypographyNormal sx={{ ml: 1 }}>
                  {t("bplrt.layerAddTicker")}
                </TypographyNormal>
              </MenuItem>
            )}
          </Menu>
        </Box>
      </StyledBox>
    </Box>
  );
};
export default ContainersList;
